import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import {
  PageContainer,
  Card,
  CardContent,
  Button,
  fadeIn,
  Textarea,
  FormRow,
  FormGroup,
  Label,
  Input,
  Select
} from '../styles/PageStyles';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { createEmailTemplate, updateEmailTemplate, getEmailTemplateById } from '../services/emailService';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toastService from '../services/toastService';
import { usePermission } from '../context/PermissionContext';

const initialTemplate = {
  code: '',
  name: '',
  category: '',
  usage: 'Medium',
  description: '',
  status: 'DRAFT',
  version: '1',
  template: '',
  type: 'body',
  company_cd: 'MMS'
};

const EmailTemplateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location.state;
  const { templateId } = useParams();
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateView, setTemplateView] = useState('editor');

  const { permissions } = usePermission();

  const userAccessPermissions = permissions?.length
    ? permissions
      .filter(ele => ele.component_type === "prompt")
      .map(ele => ele.permission.toLowerCase())
    : ['create', 'read', 'update', 'delete'];

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await getEmailTemplateById(templateId);
        if (response) {
          setSelectedTemplate(response);
        } else {
          toastService.error("Template not found");
          navigate("/email-templates");
        }
      } catch (error) {
        console.error('Error fetching email template:', error);
        toastService.error("Failed to load template");
      }
    };

    if (templateId !== 'new') {
      fetchTemplate();
    } else {
      if (stateData) {
        const { id, code, ...remainingData } = stateData;
        setSelectedTemplate({
          ...remainingData,
          name: `${remainingData.name} - Copy`,
          code: '', // Reset code to be auto-generated
          status: 'DRAFT'
        });
      } else {
        setSelectedTemplate({
          ...initialTemplate,
          company_cd: user?.companyCd || 'MMS'
        });
      }
    }
  }, [templateId, user, stateData]);

  const handleFieldChange = (field, value) => {
    setSelectedTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedTemplate.name?.trim()) {
      toastService.error("Template Name is required");
      return;
    }
    if (!selectedTemplate.template?.trim()) {
      toastService.error("Template content is required");
      return;
    }
    if (!selectedTemplate.category) {
      toastService.error("Category is required");
      return;
    }

    try {
      if (selectedTemplate.id) {
        // Edit mode - Versioning logic:
        // 1. Mark the current active/draft record as CLOSED (Archived)
        const archivePayload = {
          ...selectedTemplate,
          status: 'CLOSED',
          updated_by: user?.email || user?.username || 'system'
        };
        await updateEmailTemplate(archivePayload);

        // 2. Create a new record with the same code, incremented version, and the new changes
        const currentVersion = parseInt(selectedTemplate.version) || 1;
        const newRecordPayload = {
          code: selectedTemplate.code,
          name: selectedTemplate.name,
          category: selectedTemplate.category,
          usage: selectedTemplate.usage,
          description: selectedTemplate.description,
          status: selectedTemplate.status === 'CLOSED' ? 'DRAFT' : selectedTemplate.status,
          version: String(currentVersion + 1),
          template: selectedTemplate.template,
          type: selectedTemplate.type || 'body',
          company_cd: selectedTemplate.company_cd || 'MMS',
          created_by: user?.email || user?.username || 'system',
          updated_by: user?.email || user?.username || 'system'
        };

        await createEmailTemplate(newRecordPayload);
        toastService.success("New template version created successfully");
        navigate("/email-templates");
      } else {
        // Create mode - auto-generate code and version is 1
        const autoGeneratedCode = `TEMP-${Date.now()}`;
        const payload = {
          ...selectedTemplate,
          code: autoGeneratedCode,
          version: '1',
          created_by: user?.email || user?.username || 'system',
          updated_by: user?.email || user?.username || 'system'
        };

        await createEmailTemplate(payload);
        toastService.success("Email template created successfully");
        navigate("/email-templates");
      }
    } catch (error) {
      console.error("Error saving email template:", error);
      toastService.error("Failed to save email template");
    }
  };

  // Replace dynamic template variables with mock content for previewing
  const getPreviewHtml = (html) => {
    if (!html) return '<i>No template content provided.</i>';
    let preview = html;
    const dummyData = {
      customer_name: 'John Doe',
      customerName: 'John Doe',
      customer: 'John Doe',
      status: selectedTemplate?.status || 'Active',
      request_id: 'REQ-123456789',
      request: 'REQ-123456789',
      effective_date: '2026-06-25',
      effectiveDate: '2026-06-25',
      emailTo: 'john.doe@example.com',
      email_to: 'john.doe@example.com',
      support_url: 'https://support.metamorphtech.com',
      supportUrl: 'https://support.metamorphtech.com',
      privacy_url: 'https://privacy.metamorphtech.com',
      privacyUrl: 'https://privacy.metamorphtech.com',
      old_address: {
        street_address: '100 Old Willow Lane',
        city: 'Short Hills',
        state: 'NJ',
        zip: '07078'
      },
      new_address: {
        street_address: '200 New Maple Avenue',
        city: 'Summit',
        state: 'NJ',
        zip: '07901'
      }
    };

    Object.keys(dummyData).forEach(key => {
      const value = dummyData[key];
      if (typeof value === 'object') {
        Object.keys(value).forEach(subKey => {
          const regex = new RegExp(`{{\\s*${key}\\.${subKey}\\s*}}`, 'g');
          preview = preview.replace(regex, value[subKey]);
        });
      } else {
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        preview = preview.replace(regex, value);
      }
    });

    return preview;
  };

  const isEditable = templateId === 'new'
    ? userAccessPermissions.includes('create')
    : userAccessPermissions.includes('update');

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faEnvelope}
          goBack={true}
          title={
            selectedTemplate?.name
              ? `v${selectedTemplate.version || '1'} - ${selectedTemplate.name}`
              : "Create Email Template"
          }
          description="Design, customize, and preview HTML and plain text email templates. Define dynamic variables for workflows and trigger communication."
        />

        <Card>
          {selectedTemplate ? (
            <CardContent>
              <div style={{ marginBottom: '1rem' }}>
                <FormRow>
                  <FormGroup style={{ flex: templateId === 'new' ? '1 1 100%' : '1 1 50%' }}>
                    <Label>Template Name</Label>
                    <Input
                      type="text"
                      value={selectedTemplate.name || ''}
                      onChange={(e) => handleFieldChange("name", e.target.value)}
                      placeholder="e.g. Welcome Customer Greeting"
                      disabled={!isEditable}
                    />
                  </FormGroup>
                  {templateId !== 'new' && (
                    <FormGroup style={{ flex: '1 1 50%' }}>
                      <Label>Template Code</Label>
                      <Input
                        type="text"
                        value={selectedTemplate.code || ''}
                        placeholder="e.g. TEMP-123456789"
                        disabled={true}
                      />
                    </FormGroup>
                  )}
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Template Type</Label>
                    <Select
                      value={selectedTemplate.type || 'body'}
                      onChange={(e) => handleFieldChange("type", e.target.value)}
                      disabled={!isEditable}
                    >
                      <option value="body">Email Body Template</option>
                      <option value="attachment">Attachment Template</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Category</Label>
                    <Select
                      value={selectedTemplate.category || ''}
                      onChange={(e) => handleFieldChange("category", e.target.value)}
                      disabled={!isEditable}
                    >
                      <option value="">Select Category</option>
                      <option value="Claims">Claims</option>
                      <option value="Underwriting">Underwriting</option>
                      <option value="Customer Service">Customer Service</option>
                      <option value="Fraud">Fraud</option>
                      <option value="Document Processing">Document Processing</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Usage / Priority</Label>
                    <Select
                      value={selectedTemplate.usage || 'Medium'}
                      onChange={(e) => handleFieldChange("usage", e.target.value)}
                      disabled={!isEditable}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Status</Label>
                    <Select
                      value={selectedTemplate.status || 'DRAFT'}
                      onChange={(e) => handleFieldChange("status", e.target.value)}
                      disabled={!isEditable}
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="ACTIVE">Active</option>
                      <option value="CLOSED">Closed (Archived)</option>
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Description</Label>
                    <Textarea
                      rows="2"
                      value={selectedTemplate.description || ''}
                      onChange={(e) => handleFieldChange("description", e.target.value)}
                      placeholder="Describe the usage scenarios for this email template..."
                      disabled={!isEditable}
                    />
                  </FormGroup>
                </FormRow>

                <div style={{ marginBottom: '1.5rem', marginTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <Label style={{ fontWeight: 'bold', fontSize: '1rem', margin: 0 }}>Template HTML/Jinja Body</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        onClick={() => setTemplateView('editor')}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                        outline={templateView !== 'editor'}
                      >
                        Editor
                      </Button>
                      <Button
                        onClick={() => setTemplateView('preview')}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                        outline={templateView !== 'preview'}
                      >
                        Preview
                      </Button>
                    </div>
                  </div>

                  {templateView === 'editor' ? (
                    <Textarea
                      rows="20"
                      style={{
                        background: '#f8fafc',
                        padding: '1rem',
                        borderRadius: '8px',
                        fontFamily: 'Consolas, Monaco, "Courier New", Courier, monospace',
                        fontSize: '0.9rem',
                        whiteSpace: 'pre',
                        maxHeight: '450px',
                        overflowY: 'auto',
                        border: '1px solid rgba(0, 102, 255, 0.15)'
                      }}
                      value={selectedTemplate?.template || ''}
                      onChange={(e) => handleFieldChange('template', e.target.value)}
                      placeholder="<!DOCTYPE html>... <div>Hello {{customer_name}}</div>"
                      disabled={!isEditable}
                    />
                  ) : (
                    <iframe
                      title="Template Preview"
                      srcDoc={DOMPurify.sanitize(getPreviewHtml(selectedTemplate?.template || ''))}
                      style={{
                        width: '100%',
                        height: '450px',
                        border: '1px solid rgba(0, 102, 255, 0.15)',
                        borderRadius: '8px',
                        background: '#ffffff'
                      }}
                    />
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <Button onClick={() => navigate("/email-templates")}>
                    Cancel
                  </Button>
                  {isEditable && (
                    <Button primary onClick={handleSave}>
                      Save Changes
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          ) : (
            <CardContent>
              <div style={{
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '1rem',
                color: 'var(--text-secondary)'
              }}>
                <div style={{ fontSize: '3rem' }}>⌕</div>
                <div>Loading template details...</div>
              </div>
            </CardContent>
          )}
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default EmailTemplateDetails;
